import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchCustomers } from './customersSlice';
import { fetchCustomerGroups, createCustomerGroup, addCustomerToGroup } from '../customerGroups/customerGroupsSlice';
import Shell from '../dashboard/Shell';
import FeatureGate from '../../components/FeatureGate';
import { useToast } from '../../components/ui/Toast';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import {
  Users,
  UsersRound,
  Mail,
  Search,
  Download,
  Plus,
  Edit,
  Trash2,
  X,
  Check,
  ChevronLeft,
  ChevronRight,
  Phone,
  MapPin,
  ShoppingBag,
  Filter,
  SortAsc,
  Eye,
  UserPlus,
  Loader2,
  RefreshCw,
  Calendar,
  Building,
  FileSpreadsheet,
  ChevronDown,
  ExternalLink,
  Tag,
  Send,
  Receipt
} from 'lucide-react';
import './Customers.css';

export default function Customers() {
  const dispatch = useDispatch();
  const toast = useToast();
  const { getAxios } = useSecureAuth();

  const { items = [], status } = useSelector(s => s.customers);
  const { groups = [] } = useSelector(s => s.customerGroups);

  const safeGroups = Array.isArray(groups) ? groups : [];
  const safeItems = Array.isArray(items) ? items : [];

  // Tab state
  const [activeTab, setActiveTab] = useState('customers');

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('all');
  const [guestFilter, setGuestFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // View state
  const [viewMode, setViewMode] = useState('cards');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);

  // Modal states
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showAddCustomersToGroupModal, setShowAddCustomersToGroupModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState({ open: false, customer: null });

  // Form states
  const [customerForm, setCustomerForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: { street: '', city: '', state: '', zipCode: '' }
  });
  const [groupForm, setGroupForm] = useState({ name: '', description: '' });
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedCustomersForGroup, setSelectedCustomersForGroup] = useState([]);
  const [customerSearchInGroup, setCustomerSearchInGroup] = useState('');

  // Loading states
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Google Sheets export states
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [sheetsConnected, setSheetsConnected] = useState(false);
  const [exportingToSheets, setExportingToSheets] = useState(false);

  // Feature access for tier-gated features
  const { hasAccess: hasCampaignAccess } = useFeatureAccess('marketing.email');
  const { hasAccess: hasTagAccess } = useFeatureAccess('marketing.tags');

  // Customer orders state
  const [customerOrders, setCustomerOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersExpanded, setOrdersExpanded] = useState(false);

  // Bulk selection state. Set of customer.id so lookup + toggle stay O(1).
  // Bulk action bar mounts at the bottom whenever size > 0. Selection
  // survives across page-changes in the same session (server-side pagination
  // doesn't chunk the selection).
  const [bulkSelectedIds, setBulkSelectedIds] = useState(() => new Set());
  const [bulkTagsInput, setBulkTagsInput] = useState('');
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkTagging, setBulkTagging] = useState(false);
  const [bulkAddingToGroup, setBulkAddingToGroup] = useState(false);
  const [showBulkTagModal, setShowBulkTagModal] = useState(false);
  const [showBulkGroupModal, setShowBulkGroupModal] = useState(false);
  const [bulkGroupId, setBulkGroupId] = useState('');
  const toggleBulk = (id) => setBulkSelectedIds(cur => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const clearBulk = () => setBulkSelectedIds(new Set());
  const [resendingReceipt, setResendingReceipt] = useState(null);

  // Campaign modal state
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [campaignType, setCampaignType] = useState('email');
  const [emailForm, setEmailForm] = useState({ subject: '', message: '' });
  const [smsForm, setSmsForm] = useState({ message: '' });
  const [sendingMessage, setSendingMessage] = useState(false);

  // Tags modal state
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [customerTags, setCustomerTags] = useState([]);
  const [newTagInput, setNewTagInput] = useState('');
  const [savingTags, setSavingTags] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Fetch data on mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        dispatch(fetchCustomers()),
        dispatch(fetchCustomerGroups())
      ]);
      setLoading(false);
    };
    loadData();
  }, [dispatch]);

  // Check Google Sheets connection status
  useEffect(() => {
    const checkSheetsStatus = async () => {
      try {
        const axios = getAxios();
        const response = await axios.get('/merchant/sheets/oauth/status');
        setSheetsConnected(response.data?.connected || false);
      } catch (error) {
        console.log('Sheets status check failed:', error);
        setSheetsConnected(false);
      }
    };
    checkSheetsStatus();
  }, [getAxios]);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showExportMenu && !e.target.closest('.customers-export-dropdown')) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showExportMenu]);

  // Filter and sort customers
  const filteredCustomers = useMemo(() => {
    return safeItems.filter(customer => {
      const matchesSearch =
        `${customer.firstName} ${customer.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone?.includes(searchTerm);

      const matchesPhone =
        phoneFilter === 'all' ||
        (phoneFilter === 'with_phone' && customer.phone) ||
        (phoneFilter === 'without_phone' && !customer.phone);

      const matchesGuest =
        guestFilter === 'all' ||
        (guestFilter === 'guests' && customer.isGuest) ||
        (guestFilter === 'registered' && !customer.isGuest);

      return matchesSearch && matchesPhone && matchesGuest;
    }).sort((a, b) => {
      switch (sortBy) {
        case 'newest': return new Date(b.createdAt) - new Date(a.createdAt);
        case 'oldest': return new Date(a.createdAt) - new Date(b.createdAt);
        case 'name_asc': return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
        case 'name_desc': return `${b.firstName} ${b.lastName}`.localeCompare(`${a.firstName} ${a.lastName}`);
        case 'orders_high': return (b.orderCount || 0) - (a.orderCount || 0);
        default: return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });
  }, [safeItems, searchTerm, phoneFilter, guestFilter, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCustomers = filteredCustomers.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, phoneFilter, guestFilter, sortBy]);

  // Stats
  const stats = useMemo(() => ({
    total: safeItems.length,
    guests: safeItems.filter(c => c.isGuest).length,
    registered: safeItems.filter(c => !c.isGuest).length,
    groups: safeGroups.length,
    newsletter: safeItems.filter(c => c.newsletterSubscription).length,
    withPhone: safeItems.filter(c => c.phone).length
  }), [safeItems, safeGroups]);

  // Handle add customer
  const handleAddCustomer = async () => {
    if (!customerForm.firstName || !customerForm.email) {
      toast.error('First name and email are required');
      return;
    }

    setSaving(true);
    try {
      const axios = getAxios();
      const response = await axios.post('/merchant/customers', customerForm);

      if (response.data) {
        toast.success('Customer added successfully!');
        setShowAddCustomerModal(false);
        resetCustomerForm();
        dispatch(fetchCustomers());
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add customer');
    } finally {
      setSaving(false);
    }
  };

  // Handle edit customer
  const handleEditCustomer = async () => {
    if (!selectedCustomer) return;

    setSaving(true);
    try {
      const axios = getAxios();
      await axios.put(`/merchant/customers/${selectedCustomer.id}`, customerForm);

      toast.success('Customer updated successfully!');
      setShowEditCustomerModal(false);
      setSelectedCustomer(null);
      setShowDetailPanel(false);
      dispatch(fetchCustomers());
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update customer');
    } finally {
      setSaving(false);
    }
  };

  // Open edit modal
  const openEditModal = (customer) => {
    setSelectedCustomer(customer);
    setCustomerForm({
      firstName: customer.firstName || '',
      lastName: customer.lastName || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || { street: '', city: '', state: '', zipCode: '' }
    });
    setShowEditCustomerModal(true);
  };

  // Reset form
  const resetCustomerForm = () => {
    setCustomerForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: { street: '', city: '', state: '', zipCode: '' }
    });
  };

  // Handle create group
  const handleCreateGroup = async () => {
    if (!groupForm.name.trim()) {
      toast.error('Group name is required');
      return;
    }

    setSaving(true);
    try {
      await dispatch(createCustomerGroup({
        name: groupForm.name.trim(),
        description: groupForm.description.trim()
      })).unwrap();

      toast.success('Group created successfully!');
      setShowCreateGroupModal(false);
      setGroupForm({ name: '', description: '' });
      dispatch(fetchCustomerGroups());
    } catch (error) {
      toast.error(error.message || 'Failed to create group');
    } finally {
      setSaving(false);
    }
  };

  // Handle add customers to group
  const handleAddCustomersToGroup = async () => {
    if (!selectedGroup || selectedCustomersForGroup.length === 0) return;

    setSaving(true);
    try {
      for (const customerId of selectedCustomersForGroup) {
        await dispatch(addCustomerToGroup({
          groupId: selectedGroup.id,
          customerId
        })).unwrap();
      }

      toast.success(`Added ${selectedCustomersForGroup.length} customer(s) to group!`);
      setShowAddCustomersToGroupModal(false);
      setSelectedCustomersForGroup([]);
      dispatch(fetchCustomerGroups());
      dispatch(fetchCustomers());
    } catch (error) {
      toast.error(error.message || 'Failed to add customers to group');
    } finally {
      setSaving(false);
    }
  };

  // Export customers to CSV
  const handleExportCSV = () => {
    const headers = ['First Name', 'Last Name', 'Email', 'Phone', 'City', 'State', 'Created At'];
    const rows = filteredCustomers.map(c => [
      c.firstName || '',
      c.lastName || '',
      c.email || '',
      c.phone || '',
      c.address?.city || '',
      c.address?.state || '',
      c.createdAt ? new Date(c.createdAt).toLocaleDateString() : ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customers_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast.success('Customers exported successfully!');
  };

  // Export customers to Google Sheets
  const handleExportToSheets = async () => {
    if (!sheetsConnected) {
      toast.error('Please connect Google Sheets in Settings > Integrations first');
      return;
    }

    setExportingToSheets(true);
    setShowExportMenu(false);

    try {
      const axios = getAxios();
      const response = await axios.post('/merchant/sheets/export/customers');

      if (response.data?.success) {
        toast.success(
          <div>
            <strong>Exported {response.data.rowCount} customers!</strong>
            <br />
            <a
              href={response.data.spreadsheetUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#22c55e', textDecoration: 'underline' }}
            >
              Open in Google Sheets
            </a>
          </div>,
          { duration: 8000 }
        );
      } else {
        throw new Error(response.data?.error || 'Export failed');
      }
    } catch (error) {
      console.error('Sheets export error:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to export to Google Sheets';

      if (error.response?.data?.featureGated) {
        toast.error('Google Sheets export requires a Pro or Premium subscription');
      } else if (error.response?.data?.reconnect) {
        toast.error('Please reconnect Google Sheets in Settings');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setExportingToSheets(false);
    }
  };

  // Delete customer
  const handleDeleteCustomer = async () => {
    if (!showDeleteModal.customer) return;

    setSaving(true);
    try {
      const axios = getAxios();
      await axios.delete(`/merchant/customers/${showDeleteModal.customer.id}`);
      toast.success('Customer deleted successfully!');
      setShowDeleteModal({ open: false, customer: null });
      if (selectedCustomer?.id === showDeleteModal.customer.id) {
        setShowDetailPanel(false);
        setSelectedCustomer(null);
      }
      dispatch(fetchCustomers());
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete customer');
    } finally {
      setSaving(false);
    }
  };

  // ── Bulk actions ────────────────────────────────────
  // Dedicated bulk modal: user has customers → picks a group.
  // Kept separate from the existing add-to-group modal (which is
  // inverted: user picks a group first, then adds people to it) —
  // reusing that one leaves selectedGroup null and the confirm
  // button silently no-ops.
  const openBulkAddToGroup = () => {
    if (bulkSelectedIds.size === 0) return;
    setBulkGroupId(safeGroups[0]?.id || '');
    setShowBulkGroupModal(true);
  };

  const applyBulkAddToGroup = async () => {
    if (!bulkGroupId || bulkSelectedIds.size === 0) return;
    setBulkAddingToGroup(true);
    let ok = 0, failed = 0, skipped = 0;
    const targetGroup = safeGroups.find(g => g.id === bulkGroupId);
    const existingIds = new Set(
      (targetGroup?.customers || []).map(gc => gc.id || gc)
    );
    for (const id of bulkSelectedIds) {
      if (existingIds.has(id)) { skipped++; continue; }
      try {
        await dispatch(addCustomerToGroup({ groupId: bulkGroupId, customerId: id })).unwrap();
        ok++;
      } catch { failed++; }
    }
    setBulkAddingToGroup(false);
    setShowBulkGroupModal(false);
    clearBulk();
    dispatch(fetchCustomerGroups());
    const parts = [];
    if (ok > 0)      parts.push(`Added ${ok}`);
    if (skipped > 0) parts.push(`${skipped} already in group`);
    if (failed > 0)  parts.push(`${failed} failed`);
    (failed > 0 ? toast.error : toast.success)(parts.join(' · ') || 'Nothing to add.');
  };

  // Bulk delete — loops per customer since there's no bulk endpoint.
  // Fine for the volumes we care about (double-digit selections).
  const handleBulkDelete = async () => {
    if (bulkSelectedIds.size === 0) return;
    if (!window.confirm(`Delete ${bulkSelectedIds.size} customer(s)? This cannot be undone.`)) return;
    setBulkDeleting(true);
    const axios = getAxios();
    let ok = 0, failed = 0;
    for (const id of bulkSelectedIds) {
      try {
        await axios.delete(`/merchant/customers/${id}`);
        ok++;
      } catch { failed++; }
    }
    setBulkDeleting(false);
    clearBulk();
    dispatch(fetchCustomers());
    if (failed === 0) toast.success(`Deleted ${ok} customer(s).`);
    else              toast.error(`Deleted ${ok}, failed ${failed}.`);
  };

  // Bulk add tags. Gated on `marketing.tags`; bulk bar hides the
  // button entirely (no PRO badge on the bar) since it's a
  // secondary surface — merchants can still see the individual
  // per-customer tag flow with its own upgrade prompt.
  const applyBulkTags = async () => {
    const raw = bulkTagsInput.split(',').map(t => t.trim()).filter(Boolean);
    if (raw.length === 0 || bulkSelectedIds.size === 0) return;
    setBulkTagging(true);
    const axios = getAxios();
    let ok = 0, failed = 0;
    for (const id of bulkSelectedIds) {
      try {
        const existing = safeItems.find(c => c.id === id);
        const merged = Array.from(new Set([...(existing?.tags || []), ...raw]));
        await axios.put(`/merchant/customers/${id}`, { tags: merged });
        ok++;
      } catch { failed++; }
    }
    setBulkTagging(false);
    setBulkTagsInput('');
    setShowBulkTagModal(false);
    clearBulk();
    dispatch(fetchCustomers());
    if (failed === 0) toast.success(`Tagged ${ok} customer(s).`);
    else              toast.error(`Tagged ${ok}, failed ${failed}.`);
  };

  // Customers filtered for group modal
  const customersNotInGroup = selectedGroup
    ? safeItems.filter(c => {
        const groupCustomerIds = selectedGroup.customers?.map(gc => gc.id || gc) || [];
        return !groupCustomerIds.includes(c.id);
      }).filter(c =>
        `${c.firstName} ${c.lastName} ${c.email}`.toLowerCase().includes(customerSearchInGroup.toLowerCase())
      )
    : [];

  // Fetch customer orders when detail panel opens
  useEffect(() => {
    const fetchOrders = async () => {
      if (!selectedCustomer || !showDetailPanel) return;

      setOrdersLoading(true);
      try {
        const axios = getAxios();
        const response = await axios.get(`/merchant/customers/${selectedCustomer.id}/orders`);
        setCustomerOrders(response.data?.orders || []);
      } catch (error) {
        console.error('Failed to fetch customer orders:', error);
        setCustomerOrders([]);
      } finally {
        setOrdersLoading(false);
      }
    };

    fetchOrders();
  }, [selectedCustomer, showDetailPanel, getAxios]);

  // ESC closes the detail panel — robust escape hatch if the top-right X is
  // ever clipped by an ancestor (Shell topbar, scroll-overflow, etc).
  useEffect(() => {
    if (!showDetailPanel) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setShowDetailPanel(false);
        setSelectedCustomer(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showDetailPanel]);

  // Reset tags when opening tags modal
  useEffect(() => {
    if (showTagsModal && selectedCustomer) {
      setCustomerTags(selectedCustomer.tags || []);
      setNewTagInput('');
    }
  }, [showTagsModal, selectedCustomer]);

  // Set default campaign type based on feature access
  useEffect(() => {
    if (showCampaignModal) {
      setCampaignType(hasCampaignAccess ? 'email' : 'sms');
    }
  }, [showCampaignModal, hasCampaignAccess]);

  // Handle resend receipt
  const handleResendReceipt = async (orderId) => {
    setResendingReceipt(orderId);
    try {
      const axios = getAxios();
      await axios.post(`/merchant/customers/${selectedCustomer.id}/orders/${orderId}/resend-receipt`);
      toast.success('Receipt sent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send receipt');
    } finally {
      setResendingReceipt(null);
    }
  };

  // Handle send email to customer
  const handleSendEmail = async () => {
    if (!emailForm.subject.trim() || !emailForm.message.trim()) {
      toast.error('Subject and message are required');
      return;
    }

    setSendingMessage(true);
    try {
      const axios = getAxios();
      await axios.post(`/merchant/customers/${selectedCustomer.id}/send-email`, {
        subject: emailForm.subject.trim(),
        message: emailForm.message.trim()
      });
      toast.success('Email sent!');
      setShowCampaignModal(false);
      setEmailForm({ subject: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send email');
    } finally {
      setSendingMessage(false);
    }
  };

  // Handle send SMS to customer
  const handleSendSMS = async () => {
    if (!smsForm.message.trim()) {
      toast.error('Message is required');
      return;
    }

    setSendingMessage(true);
    try {
      const axios = getAxios();
      await axios.post(`/merchant/customers/${selectedCustomer.id}/send-sms`, {
        message: smsForm.message.trim()
      });
      toast.success('SMS sent!');
      setShowCampaignModal(false);
      setSmsForm({ message: '' });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send SMS');
    } finally {
      setSendingMessage(false);
    }
  };

  // Handle add tag
  const handleAddTag = () => {
    const tag = newTagInput.trim();
    if (!tag) return;
    if (customerTags.includes(tag)) {
      toast.error('Tag already exists');
      return;
    }
    setCustomerTags([...customerTags, tag]);
    setNewTagInput('');
  };

  // Handle remove tag
  const handleRemoveTag = (tagToRemove) => {
    setCustomerTags(customerTags.filter(tag => tag !== tagToRemove));
  };

  // Handle save tags
  const handleSaveTags = async () => {
    setSavingTags(true);
    try {
      const axios = getAxios();
      await axios.put(`/merchant/customers/${selectedCustomer.id}/tags`, { tags: customerTags });
      toast.success('Tags updated successfully!');
      setShowTagsModal(false);
      dispatch(fetchCustomers()); // Refresh customers list
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update tags');
    } finally {
      setSavingTags(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleCustomerClick = (customer) => {
    setSelectedCustomer(customer);
    setShowDetailPanel(true);
    setOrdersExpanded(false);
  };

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([
      dispatch(fetchCustomers()),
      dispatch(fetchCustomerGroups())
    ]);
    setLoading(false);
  };

  return (
    <Shell>
      <FeatureGate featureId="analytics.customers" upgradeMessage="Upgrade to access customer management features">
        <div className="customers-modern-container cv2">
          <div className={`customers-modern-layout ${showDetailPanel ? 'panel-open' : ''}`}>
            {/* Main Content */}
            <div className="customers-main-content">
              {/* Header — title + tagline + overflow actions. No decorative icon,
                  no double H1, no rainbow stat cards. */}
              <div className="cv2-header">
                <div className="cv2-header__text">
                  <h1 className="cv2-header__title">Customers</h1>
                  <p className="cv2-header__sub">
                    {stats.total} total · {stats.registered} registered · {stats.guests} guests · {stats.groups} groups · {stats.newsletter} newsletter
                  </p>
                </div>
                <div className="cv2-header__actions">
                  <button
                    className="cv2-iconbtn"
                    onClick={handleRefresh}
                    disabled={loading}
                    aria-label="Refresh"
                    title="Refresh"
                  >
                    <RefreshCw size={16} className={loading ? 'spinning' : ''} />
                  </button>
                  <div className="cv2-overflow">
                    <button
                      className="cv2-iconbtn"
                      onClick={() => setShowExportMenu(!showExportMenu)}
                      disabled={exportingToSheets}
                      aria-label="Export"
                      title="Export"
                    >
                      {exportingToSheets ? <Loader2 size={16} className="spinning" /> : <Download size={16} />}
                    </button>
                    {showExportMenu && (
                      <div className="cv2-overflow__menu">
                        <button
                          className="cv2-overflow__item"
                          onClick={() => { handleExportCSV(); setShowExportMenu(false); }}
                        >
                          <Download size={14} />
                          <span>Export to CSV</span>
                        </button>
                        <button
                          className="cv2-overflow__item"
                          onClick={handleExportToSheets}
                          disabled={!sheetsConnected}
                        >
                          <FileSpreadsheet size={14} />
                          <span>{sheetsConnected ? 'Export to Google Sheets' : 'Connect Sheets in Settings'}</span>
                          {sheetsConnected && <ExternalLink size={12} />}
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    className="cv2-btn cv2-btn--primary"
                    onClick={() => setShowAddCustomerModal(true)}
                  >
                    <UserPlus size={16} />
                    <span className="cv2-btn__label">Add</span>
                  </button>
                </div>
              </div>

              {/* Tabs — pill style per CLAUDE.md, with inline counts */}
              <div className="cv2-tabs">
                <button
                  className={`cv2-tab ${activeTab === 'customers' ? 'is-active' : ''}`}
                  onClick={() => setActiveTab('customers')}
                >
                  Customers
                  <span className="cv2-tab__count">{stats.total}</span>
                </button>
                <button
                  className={`cv2-tab ${activeTab === 'groups' ? 'is-active' : ''}`}
                  onClick={() => setActiveTab('groups')}
                >
                  Groups
                  <span className="cv2-tab__count">{stats.groups}</span>
                </button>
                <button
                  className={`cv2-tab ${activeTab === 'newsletter' ? 'is-active' : ''}`}
                  onClick={() => setActiveTab('newsletter')}
                >
                  Newsletter
                  <span className="cv2-tab__count">{stats.newsletter}</span>
                </button>
              </div>

              {/* Customers Tab */}
              {activeTab === 'customers' && (
                <>
                  {/* Search + filter pills. Filters collapse into a dropdown
                      on mobile so the search input gets the full row. */}
                  <div className="cv2-filters">
                    <div className="cv2-search">
                      <Search size={16} aria-hidden />
                      <input
                        type="text"
                        placeholder="Search by name, email or phone…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {searchTerm && (
                        <button className="cv2-search__clear" onClick={() => setSearchTerm('')} aria-label="Clear search">
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    <div className="cv2-filter-pills">
                      <select
                        className="cv2-select"
                        value={guestFilter}
                        onChange={(e) => setGuestFilter(e.target.value)}
                        aria-label="Customer type"
                      >
                        <option value="all">All types</option>
                        <option value="registered">Registered only</option>
                        <option value="guests">Guests only</option>
                      </select>
                      <select
                        className="cv2-select"
                        value={phoneFilter}
                        onChange={(e) => setPhoneFilter(e.target.value)}
                        aria-label="Phone filter"
                      >
                        <option value="all">Has phone or not</option>
                        <option value="with_phone">With phone</option>
                        <option value="without_phone">Without phone</option>
                      </select>
                      <select
                        className="cv2-select"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        aria-label="Sort"
                      >
                        <option value="newest">Newest</option>
                        <option value="oldest">Oldest</option>
                        <option value="name_asc">Name A–Z</option>
                        <option value="name_desc">Name Z–A</option>
                        <option value="orders_high">Most orders</option>
                      </select>
                    </div>
                  </div>

                  {/* Loading/Empty States */}
                  {loading ? (
                    <div className="cv2-loading">
                      <Loader2 size={28} className="spinning" />
                      <p>Loading customers…</p>
                    </div>
                  ) : filteredCustomers.length === 0 ? (
                    <div className="cv2-empty">
                      <h3 className="cv2-empty__title">No customers yet</h3>
                      <p className="cv2-empty__body">
                        {searchTerm || phoneFilter !== 'all' || guestFilter !== 'all'
                          ? 'Nothing matches those filters.'
                          : 'When someone checks out or you add them manually, they\'ll show up here.'}
                      </p>
                      {searchTerm || phoneFilter !== 'all' || guestFilter !== 'all' ? (
                        <button
                          className="cv2-btn cv2-btn--ghost"
                          onClick={() => { setSearchTerm(''); setPhoneFilter('all'); setGuestFilter('all'); }}
                        >
                          Clear filters
                        </button>
                      ) : (
                        <button
                          className="cv2-btn cv2-btn--primary"
                          onClick={() => setShowAddCustomerModal(true)}
                        >
                          <Plus size={16} /> Add customer
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      <p className="cv2-results">
                        Showing {paginatedCustomers.length} of {filteredCustomers.length}
                      </p>

                      {/* Unified list. Row collapses to a two-line card on
                          mobile (avatar+name+orders top; email/phone below). */}
                      <ul className="cv2-list">
                        {/* Select-all header — only shows when list has rows. */}
                        {paginatedCustomers.length > 0 && (
                          <li className="cv2-row cv2-row--selectall">
                            <label className="cv2-row__check" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                aria-label="Select all on this page"
                                checked={paginatedCustomers.length > 0 && paginatedCustomers.every(c => bulkSelectedIds.has(c.id))}
                                onChange={(e) => {
                                  setBulkSelectedIds(cur => {
                                    const next = new Set(cur);
                                    if (e.target.checked) paginatedCustomers.forEach(c => next.add(c.id));
                                    else                  paginatedCustomers.forEach(c => next.delete(c.id));
                                    return next;
                                  });
                                }}
                              />
                            </label>
                            <span className="cv2-row__selectall-label">
                              {bulkSelectedIds.size > 0
                                ? `${bulkSelectedIds.size} selected`
                                : 'Select all on this page'}
                            </span>
                          </li>
                        )}
                        {paginatedCustomers.map((customer) => {
                          const isSelected = selectedCustomer?.id === customer.id;
                          const isBulkSel  = bulkSelectedIds.has(customer.id);
                          const fullName = `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'Unnamed';
                          const initials = `${(customer.firstName || '?').charAt(0)}${(customer.lastName || '').charAt(0)}`.toUpperCase();
                          const ltv      = Number(customer.totalSpent) || 0;
                          const churn    = customer.churnRisk;
                          return (
                            <li
                              key={customer.id}
                              className={`cv2-row ${isSelected ? 'is-selected' : ''} ${isBulkSel ? 'is-bulk-selected' : ''}`}
                              onClick={() => handleCustomerClick(customer)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleCustomerClick(customer); }}
                            >
                              <label
                                className="cv2-row__check"
                                onClick={(e) => e.stopPropagation()}
                                title={isBulkSel ? 'Deselect' : 'Select'}
                              >
                                <input
                                  type="checkbox"
                                  checked={isBulkSel}
                                  onChange={() => toggleBulk(customer.id)}
                                  aria-label={`Select ${fullName}`}
                                />
                              </label>
                              <span className="cv2-row__avatar" aria-hidden>{initials}</span>
                              <div className="cv2-row__main">
                                <div className="cv2-row__line">
                                  <span className="cv2-row__name">{fullName}</span>
                                  {customer.isGuest && <span className="cv2-pill cv2-pill--guest">Guest</span>}
                                  {churn && (
                                    <span
                                      className={`cv2-pill cv2-churn cv2-churn--${churn.toLowerCase()}`}
                                      title={
                                        churn === 'ACTIVE'       ? 'Bought within last 30 days'
                                      : churn === 'AT_RISK'      ? 'Last purchase 30–90 days ago'
                                      : churn === 'DORMANT'      ? 'No purchase in 90+ days'
                                      : churn === 'NEW'          ? 'Signed up recently, no purchase yet'
                                      : churn === 'NEVER_BOUGHT' ? 'Signed up > 30 days ago, never bought'
                                      : churn
                                      }
                                    >
                                      {churn.replace('_', ' ').toLowerCase()}
                                    </span>
                                  )}
                                  {Array.isArray(customer.tags) && customer.tags.length > 0 && (
                                    <span
                                      className="cv2-pill cv2-pill--tags-indicator"
                                      title={customer.tags.join(', ')}
                                      aria-label={`${customer.tags.length} tag${customer.tags.length === 1 ? '' : 's'}`}
                                    >
                                      <Tag size={10} aria-hidden />
                                      {customer.tags.length > 1 ? customer.tags.length : ''}
                                    </span>
                                  )}
                                </div>
                                <div className="cv2-row__meta">
                                  <span className="cv2-row__email">{customer.email || '—'}</span>
                                  {customer.phone && <span className="cv2-row__dot" aria-hidden>·</span>}
                                  {customer.phone && <span className="cv2-row__phone">{customer.phone}</span>}
                                </div>
                              </div>
                              <span className="cv2-row__ltv" title="Lifetime spend (delivered orders)">
                                <strong>{formatCurrency(ltv)}</strong>
                                <small>{customer.orderCount || 0} order{(customer.orderCount || 0) === 1 ? '' : 's'}</small>
                              </span>
                              <div className="cv2-row__actions" onClick={(e) => e.stopPropagation()}>
                                <button
                                  className="cv2-iconbtn cv2-iconbtn--sm"
                                  onClick={() => openEditModal(customer)}
                                  aria-label="Edit"
                                  title="Edit"
                                >
                                  <Edit size={14} />
                                </button>
                                <button
                                  className="cv2-iconbtn cv2-iconbtn--sm cv2-iconbtn--danger"
                                  onClick={() => setShowDeleteModal({ open: true, customer })}
                                  aria-label="Delete"
                                  title="Delete"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <nav className="cv2-pager" aria-label="Pagination">
                          <button
                            className="cv2-iconbtn"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            aria-label="Previous page"
                          >
                            <ChevronLeft size={16} />
                          </button>
                          <span className="cv2-pager__info">Page {currentPage} of {totalPages}</span>
                          <button
                            className="cv2-iconbtn"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            aria-label="Next page"
                          >
                            <ChevronRight size={16} />
                          </button>
                        </nav>
                      )}
                    </>
                  )}
                </>
              )}

              {/* Groups Tab */}
              {activeTab === 'groups' && (
                <div className="cv2-pane">
                  {/* Pane header — title strip with action; no rainbow icon */}
                  <div className="cv2-pane__head">
                    <div className="cv2-pane__title">
                      <h2>Customer groups</h2>
                      <p>Segment your customers for targeted campaigns and reporting.</p>
                    </div>
                    <button
                      className="cv2-btn cv2-btn--primary"
                      onClick={() => setShowCreateGroupModal(true)}
                    >
                      <Plus size={14} />
                      <span>New group</span>
                    </button>
                  </div>

                  {safeGroups.length === 0 ? (
                    <div className="cv2-empty">
                      <h3 className="cv2-empty__title">No customer groups yet</h3>
                      <p className="cv2-empty__body">
                        Group customers by behavior, channel, or campaign to message them as a cohort.
                      </p>
                      <button
                        className="cv2-btn cv2-btn--primary"
                        onClick={() => setShowCreateGroupModal(true)}
                      >
                        <Plus size={14} />
                        <span>Create your first group</span>
                      </button>
                    </div>
                  ) : (
                    <div className="cv2-group__grid">
                      {safeGroups.map(group => {
                        const count = group.customerCount || group.customers?.length || 0;
                        return (
                          <article key={group.id} className="cv2-group__card">
                            <header className="cv2-group__head">
                              <span className="cv2-group__avatar" aria-hidden>
                                <UsersRound size={16} />
                              </span>
                              <div className="cv2-group__title">
                                <h3>{group.name}</h3>
                                <span className="cv2-group__count">
                                  {count} {count === 1 ? 'customer' : 'customers'}
                                </span>
                              </div>
                            </header>
                            {group.description && (
                              <p className="cv2-group__desc">{group.description}</p>
                            )}
                            <footer className="cv2-group__foot">
                              <button
                                className="cv2-btn cv2-btn--ghost"
                                onClick={() => {
                                  setSelectedGroup(group);
                                  setShowAddCustomersToGroupModal(true);
                                }}
                              >
                                <UserPlus size={14} />
                                <span>Add customers</span>
                              </button>
                            </footer>
                          </article>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Newsletter Tab */}
              {activeTab === 'newsletter' && (() => {
                const subscribers = safeItems.filter(c => c.newsletterSubscription);
                return (
                  <div className="cv2-pane">
                    <div className="cv2-pane__head">
                      <div className="cv2-pane__title">
                        <h2>Newsletter subscribers</h2>
                        <p>
                          {subscribers.length}{' '}
                          {subscribers.length === 1 ? 'customer has' : 'customers have'} opted in to your newsletter.
                        </p>
                      </div>
                    </div>

                    {subscribers.length === 0 ? (
                      <div className="cv2-empty">
                        <h3 className="cv2-empty__title">No newsletter subscribers yet</h3>
                        <p className="cv2-empty__body">
                          Customers who opt in during checkout or from the storefront will show up here.
                        </p>
                      </div>
                    ) : (
                      <ul className="cv2-list">
                        {subscribers.map(customer => {
                          const name = `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'Unnamed';
                          return (
                            <li
                              key={customer.id}
                              className="cv2-row"
                              role="button"
                              tabIndex={0}
                              onClick={() => { setSelectedCustomer(customer); setShowDetailPanel(true); }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setSelectedCustomer(customer);
                                  setShowDetailPanel(true);
                                }
                              }}
                            >
                              <span className="cv2-row__avatar" aria-hidden>
                                {(customer.firstName || '?').charAt(0)}{(customer.lastName || '').charAt(0)}
                              </span>
                              <div className="cv2-row__main">
                                <div className="cv2-row__line">
                                  <span className="cv2-row__name">{name}</span>
                                </div>
                                <div className="cv2-row__meta">
                                  {customer.email && <span className="cv2-row__email">{customer.email}</span>}
                                </div>
                              </div>
                              <div className="cv2-row__orders" title="Subscribed">
                                <Mail size={12} />
                                <small>{customer.createdAt ? formatDate(customer.createdAt) : '—'}</small>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Detail Panel */}
            {/* Backdrop (mobile only) — tap outside to close the detail sheet */}
            {showDetailPanel && (
              <div
                className="cv2-detail-backdrop"
                onClick={() => { setShowDetailPanel(false); setSelectedCustomer(null); }}
                aria-hidden
              />
            )}

            <aside className={`cv2-detail ${showDetailPanel ? 'is-open' : ''}`} aria-hidden={!showDetailPanel}>
              {selectedCustomer && (
                <>
                  {/* Header — close button + drag handle on mobile */}
                  <header className="cv2-detail__head">
                    <span className="cv2-detail__grabber" aria-hidden />
                    <span className="cv2-detail__eyebrow">Customer</span>
                    <button
                      className="cv2-iconbtn cv2-detail__close"
                      onClick={() => { setShowDetailPanel(false); setSelectedCustomer(null); }}
                      aria-label="Close"
                    >
                      <X size={16} />
                    </button>
                  </header>

                  <div className="cv2-detail__body">
                    {/* Identity */}
                    <section className="cv2-detail__identity">
                      <span className="cv2-detail__avatar" aria-hidden>
                        {(selectedCustomer.firstName || '?').charAt(0)}{(selectedCustomer.lastName || '').charAt(0)}
                      </span>
                      <h2 className="cv2-detail__name">
                        {`${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim() || 'Unnamed'}
                      </h2>
                      {selectedCustomer.isGuest && (
                        <span className="cv2-pill cv2-pill--guest" style={{ marginTop: 4 }}>Guest</span>
                      )}
                    </section>

                    {/* Contact actions — Call / WhatsApp / Email */}
                    {(selectedCustomer.phone || selectedCustomer.email) && (
                      <section className="cv2-detail__contact-row">
                        {selectedCustomer.phone && (
                          <a
                            href={`tel:${selectedCustomer.phone.replace(/[^0-9+]/g, '')}`}
                            className="cv2-quick"
                            title="Call"
                          >
                            <Phone size={14} />
                            <span>Call</span>
                          </a>
                        )}
                        {selectedCustomer.phone && (
                          <a
                            href={`https://wa.me/${selectedCustomer.phone.replace(/[^0-9]/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="cv2-quick cv2-quick--whatsapp"
                            title="WhatsApp"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                            </svg>
                            <span>WhatsApp</span>
                          </a>
                        )}
                        {selectedCustomer.email && (
                          <a
                            href={`mailto:${selectedCustomer.email}`}
                            className="cv2-quick"
                            title="Email"
                          >
                            <Mail size={14} />
                            <span>Email</span>
                          </a>
                        )}
                      </section>
                    )}

                    {/* Message + Tags (feature-gated) */}
                    <section className="cv2-detail__feature-row">
                      <button
                        type="button"
                        className={`cv2-quick ${!hasCampaignAccess ? 'is-locked' : ''}`}
                        onClick={() => hasCampaignAccess && setShowCampaignModal(true)}
                        disabled={!hasCampaignAccess}
                        title={hasCampaignAccess ? 'Send a message' : 'Upgrade to PRO'}
                      >
                        <Send size={14} />
                        <span>Message</span>
                        {!hasCampaignAccess && <span className="cv2-pro-badge">PRO</span>}
                      </button>
                      <button
                        type="button"
                        className={`cv2-quick ${!hasTagAccess ? 'is-locked' : ''}`}
                        onClick={() => hasTagAccess && setShowTagsModal(true)}
                        disabled={!hasTagAccess}
                        title={hasTagAccess ? 'Manage tags' : 'Upgrade to PRO'}
                      >
                        <Tag size={14} />
                        <span>Tags</span>
                        {!hasTagAccess && <span className="cv2-pro-badge">PRO</span>}
                      </button>
                    </section>

                    {/* Contact info — flat definition list, no rainbow icon cards */}
                    <section className="cv2-detail__group">
                      <h3 className="cv2-detail__group-title">Contact</h3>
                      <dl className="cv2-detail__list">
                        <div className="cv2-detail__row"><dt>Email</dt><dd>{selectedCustomer.email || <em className="cv2-detail__empty">—</em>}</dd></div>
                        <div className="cv2-detail__row"><dt>Phone</dt><dd>{selectedCustomer.phone || <em className="cv2-detail__empty">—</em>}</dd></div>
                        {selectedCustomer.address?.street && (
                          <div className="cv2-detail__row">
                            <dt>Address</dt>
                            <dd>
                              {selectedCustomer.address.street}
                              {selectedCustomer.address.city && <>, {selectedCustomer.address.city}</>}
                              {selectedCustomer.address.state && <>, {selectedCustomer.address.state}</>}
                              {selectedCustomer.address.zipCode && <> {selectedCustomer.address.zipCode}</>}
                            </dd>
                          </div>
                        )}
                      </dl>
                    </section>

                    {/* Activity / lifetime stats */}
                    <section className="cv2-detail__group">
                      <h3 className="cv2-detail__group-title">Lifetime</h3>
                      <dl className="cv2-detail__list">
                        <div className="cv2-detail__row"><dt>Total spent</dt><dd className="cv2-detail__strong">{formatCurrency(selectedCustomer.totalSpent)}</dd></div>
                        <div className="cv2-detail__row"><dt>Orders</dt><dd>{customerOrders.length || selectedCustomer.orderCount || 0}</dd></div>
                        {selectedCustomer.avgOrderValue > 0 && (
                          <div className="cv2-detail__row"><dt>Avg order value</dt><dd>{formatCurrency(selectedCustomer.avgOrderValue)}</dd></div>
                        )}
                        {selectedCustomer.churnRisk && (
                          <div className="cv2-detail__row">
                            <dt>Status</dt>
                            <dd>
                              <span className={`cv2-pill cv2-churn cv2-churn--${selectedCustomer.churnRisk.toLowerCase()}`}>
                                {selectedCustomer.churnRisk.replace('_', ' ').toLowerCase()}
                              </span>
                            </dd>
                          </div>
                        )}
                        {selectedCustomer.firstPurchaseAt && (
                          <div className="cv2-detail__row"><dt>First purchase</dt><dd>{formatDate(selectedCustomer.firstPurchaseAt)}</dd></div>
                        )}
                        {selectedCustomer.lastPurchaseAt && (
                          <div className="cv2-detail__row">
                            <dt>Last purchase</dt>
                            <dd>
                              {formatDate(selectedCustomer.lastPurchaseAt)}
                              {selectedCustomer.daysSinceLastPurchase != null && (
                                <span className="cv2-detail__muted"> · {selectedCustomer.daysSinceLastPurchase}d ago</span>
                              )}
                            </dd>
                          </div>
                        )}
                        <div className="cv2-detail__row"><dt>Customer since</dt><dd>{selectedCustomer.createdAt ? formatDate(selectedCustomer.createdAt) : '—'}</dd></div>
                        {selectedCustomer.newsletterSubscription && (
                          <div className="cv2-detail__row"><dt>Newsletter</dt><dd className="cv2-detail__strong">Subscribed</dd></div>
                        )}
                      </dl>
                    </section>

                    {/* Orders — collapsible */}
                    {customerOrders.length > 0 && (
                      <section className="cv2-detail__group">
                        <button
                          type="button"
                          className="cv2-detail__group-toggle"
                          onClick={() => setOrdersExpanded(!ordersExpanded)}
                          aria-expanded={ordersExpanded}
                        >
                          <span className="cv2-detail__group-title">Recent orders</span>
                          <span className="cv2-detail__count">{customerOrders.length}</span>
                          <ChevronDown
                            size={14}
                            className={`cv2-detail__chev ${ordersExpanded ? 'is-open' : ''}`}
                            aria-hidden
                          />
                        </button>
                        {ordersExpanded && (
                          <ul className="cv2-detail__orders">
                            {customerOrders.map(order => (
                              <li key={order.id} className="cv2-detail__order">
                                <div className="cv2-detail__order-main">
                                  <span className="cv2-detail__order-num">#{order.orderNumber || order.id.slice(-8)}</span>
                                  <span className={`cv2-detail__order-status status-${(order.status || '').toLowerCase()}`}>{order.status}</span>
                                </div>
                                <div className="cv2-detail__order-side">
                                  <span className="cv2-detail__order-total">{formatCurrency(order.total)}</span>
                                  <button
                                    className="cv2-iconbtn cv2-iconbtn--sm"
                                    onClick={() => handleResendReceipt(order.id)}
                                    disabled={resendingReceipt === order.id}
                                    title="Resend receipt"
                                    aria-label="Resend receipt"
                                  >
                                    {resendingReceipt === order.id
                                      ? <Loader2 size={14} className="spinning" />
                                      : <Receipt size={14} />}
                                  </button>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </section>
                    )}

                    {/* Tags */}
                    {selectedCustomer.tags && selectedCustomer.tags.length > 0 && (
                      <section className="cv2-detail__group">
                        <h3 className="cv2-detail__group-title">Tags</h3>
                        <div className="cv2-detail__chips">
                          {selectedCustomer.tags.map((t) => (
                            <span key={t} className="cv2-pill cv2-pill--tag">{t}</span>
                          ))}
                        </div>
                      </section>
                    )}

                    {/* Groups */}
                    {(() => {
                      const customerGroups = safeGroups.filter(g =>
                        selectedCustomer.groups && selectedCustomer.groups.includes(g.id)
                      );
                      if (customerGroups.length === 0) return null;
                      return (
                        <section className="cv2-detail__group">
                          <h3 className="cv2-detail__group-title">Groups</h3>
                          <div className="cv2-detail__chips">
                            {customerGroups.map(g => (
                              <span key={g.id} className="cv2-pill cv2-pill--tag">{g.name}</span>
                            ))}
                          </div>
                        </section>
                      );
                    })()}

                    {/* Segments */}
                    {selectedCustomer.segments && selectedCustomer.segments.length > 0 && (
                      <section className="cv2-detail__group">
                        <h3 className="cv2-detail__group-title">Segments</h3>
                        <div className="cv2-detail__chips">
                          {selectedCustomer.segments.map((seg, i) => (
                            <span key={seg.id || i} className="cv2-pill cv2-pill--tag">{seg.name}</span>
                          ))}
                        </div>
                      </section>
                    )}
                  </div>

                  {/* Sticky footer — primary edit + danger delete */}
                  <footer className="cv2-detail__foot">
                    <button
                      className="cv2-btn cv2-btn--ghost cv2-detail__danger"
                      onClick={() => setShowDeleteModal({ open: true, customer: selectedCustomer })}
                    >
                      <Trash2 size={14} />
                      <span>Delete</span>
                    </button>
                    <button
                      className="cv2-btn cv2-btn--primary"
                      onClick={() => openEditModal(selectedCustomer)}
                    >
                      <Edit size={14} />
                      <span>Edit</span>
                    </button>
                  </footer>
                </>
              )}
            </aside>
          </div>

          {/* Add Customer Modal */}
          {showAddCustomerModal && (
            <div className="customers-modal-overlay" onClick={() => setShowAddCustomerModal(false)}>
              <div className="customers-modal" onClick={(e) => e.stopPropagation()}>
                <div className="customers-modal-header">
                  <div className="customers-modal-icon add">
                    <UserPlus size={24} />
                  </div>
                  <h2>Add New Customer</h2>
                  <p>Enter customer details below</p>
                </div>

                <div className="customers-modal-body">
                  <div className="modal-form-row">
                    <div className="modal-form-field">
                      <label>First Name *</label>
                      <input
                        type="text"
                        value={customerForm.firstName}
                        onChange={(e) => setCustomerForm({...customerForm, firstName: e.target.value})}
                        placeholder="Enter first name"
                      />
                    </div>
                    <div className="modal-form-field">
                      <label>Last Name</label>
                      <input
                        type="text"
                        value={customerForm.lastName}
                        onChange={(e) => setCustomerForm({...customerForm, lastName: e.target.value})}
                        placeholder="Enter last name"
                      />
                    </div>
                  </div>

                  <div className="modal-form-row">
                    <div className="modal-form-field">
                      <label>Email *</label>
                      <input
                        type="email"
                        value={customerForm.email}
                        onChange={(e) => setCustomerForm({...customerForm, email: e.target.value})}
                        placeholder="Enter email"
                      />
                    </div>
                    <div className="modal-form-field">
                      <label>Phone</label>
                      <input
                        type="tel"
                        value={customerForm.phone}
                        onChange={(e) => setCustomerForm({...customerForm, phone: e.target.value})}
                        placeholder="Enter phone number"
                      />
                    </div>
                  </div>

                  <div className="modal-form-field">
                    <label>Street Address</label>
                    <input
                      type="text"
                      value={customerForm.address?.street || ''}
                      onChange={(e) => setCustomerForm({
                        ...customerForm,
                        address: {...customerForm.address, street: e.target.value}
                      })}
                      placeholder="Enter street address"
                    />
                  </div>

                  <div className="modal-form-row three">
                    <div className="modal-form-field">
                      <label>City</label>
                      <input
                        type="text"
                        value={customerForm.address?.city || ''}
                        onChange={(e) => setCustomerForm({
                          ...customerForm,
                          address: {...customerForm.address, city: e.target.value}
                        })}
                        placeholder="City"
                      />
                    </div>
                    <div className="modal-form-field">
                      <label>State</label>
                      <input
                        type="text"
                        value={customerForm.address?.state || ''}
                        onChange={(e) => setCustomerForm({
                          ...customerForm,
                          address: {...customerForm.address, state: e.target.value}
                        })}
                        placeholder="State"
                      />
                    </div>
                    <div className="modal-form-field">
                      <label>ZIP Code</label>
                      <input
                        type="text"
                        value={customerForm.address?.zipCode || ''}
                        onChange={(e) => setCustomerForm({
                          ...customerForm,
                          address: {...customerForm.address, zipCode: e.target.value}
                        })}
                        placeholder="ZIP"
                      />
                    </div>
                  </div>
                </div>

                <div className="customers-modal-footer">
                  <button
                    className="modal-btn-cancel"
                    onClick={() => {
                      setShowAddCustomerModal(false);
                      resetCustomerForm();
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="modal-btn-primary"
                    onClick={handleAddCustomer}
                    disabled={saving || !customerForm.firstName || !customerForm.email}
                  >
                    {saving ? <Loader2 size={18} className="spinning" /> : <Plus size={18} />}
                    Add Customer
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Customer Modal */}
          {showEditCustomerModal && (
            <div className="customers-modal-overlay" onClick={() => setShowEditCustomerModal(false)}>
              <div className="customers-modal" onClick={(e) => e.stopPropagation()}>
                <div className="customers-modal-header">
                  <div className="customers-modal-icon edit">
                    <Edit size={24} />
                  </div>
                  <h2>Edit Customer</h2>
                  <p>Update customer information</p>
                </div>

                <div className="customers-modal-body">
                  <div className="modal-form-row">
                    <div className="modal-form-field">
                      <label>First Name *</label>
                      <input
                        type="text"
                        value={customerForm.firstName}
                        onChange={(e) => setCustomerForm({...customerForm, firstName: e.target.value})}
                        placeholder="Enter first name"
                      />
                    </div>
                    <div className="modal-form-field">
                      <label>Last Name</label>
                      <input
                        type="text"
                        value={customerForm.lastName}
                        onChange={(e) => setCustomerForm({...customerForm, lastName: e.target.value})}
                        placeholder="Enter last name"
                      />
                    </div>
                  </div>

                  <div className="modal-form-row">
                    <div className="modal-form-field">
                      <label>Email *</label>
                      <input
                        type="email"
                        value={customerForm.email}
                        onChange={(e) => setCustomerForm({...customerForm, email: e.target.value})}
                        placeholder="Enter email"
                      />
                    </div>
                    <div className="modal-form-field">
                      <label>Phone</label>
                      <input
                        type="tel"
                        value={customerForm.phone}
                        onChange={(e) => setCustomerForm({...customerForm, phone: e.target.value})}
                        placeholder="Enter phone number"
                      />
                    </div>
                  </div>

                  <div className="modal-form-field">
                    <label>Street Address</label>
                    <input
                      type="text"
                      value={customerForm.address?.street || ''}
                      onChange={(e) => setCustomerForm({
                        ...customerForm,
                        address: {...customerForm.address, street: e.target.value}
                      })}
                      placeholder="Enter street address"
                    />
                  </div>

                  <div className="modal-form-row three">
                    <div className="modal-form-field">
                      <label>City</label>
                      <input
                        type="text"
                        value={customerForm.address?.city || ''}
                        onChange={(e) => setCustomerForm({
                          ...customerForm,
                          address: {...customerForm.address, city: e.target.value}
                        })}
                        placeholder="City"
                      />
                    </div>
                    <div className="modal-form-field">
                      <label>State</label>
                      <input
                        type="text"
                        value={customerForm.address?.state || ''}
                        onChange={(e) => setCustomerForm({
                          ...customerForm,
                          address: {...customerForm.address, state: e.target.value}
                        })}
                        placeholder="State"
                      />
                    </div>
                    <div className="modal-form-field">
                      <label>ZIP Code</label>
                      <input
                        type="text"
                        value={customerForm.address?.zipCode || ''}
                        onChange={(e) => setCustomerForm({
                          ...customerForm,
                          address: {...customerForm.address, zipCode: e.target.value}
                        })}
                        placeholder="ZIP"
                      />
                    </div>
                  </div>
                </div>

                <div className="customers-modal-footer">
                  <button
                    className="modal-btn-cancel"
                    onClick={() => {
                      setShowEditCustomerModal(false);
                      setSelectedCustomer(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="modal-btn-primary"
                    onClick={handleEditCustomer}
                    disabled={saving}
                  >
                    {saving ? <Loader2 size={18} className="spinning" /> : <Check size={18} />}
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Create Group Modal */}
          {showCreateGroupModal && (
            <div className="customers-modal-overlay" onClick={() => setShowCreateGroupModal(false)}>
              <div className="customers-modal small" onClick={(e) => e.stopPropagation()}>
                <div className="customers-modal-header">
                  <div className="customers-modal-icon group">
                    <UsersRound size={24} />
                  </div>
                  <h2>Create Customer Group</h2>
                  <p>Organize customers into groups</p>
                </div>

                <div className="customers-modal-body">
                  <div className="modal-form-field">
                    <label>Group Name *</label>
                    <input
                      type="text"
                      value={groupForm.name}
                      onChange={(e) => setGroupForm({...groupForm, name: e.target.value})}
                      placeholder="Enter group name"
                    />
                  </div>

                  <div className="modal-form-field">
                    <label>Description</label>
                    <textarea
                      value={groupForm.description}
                      onChange={(e) => setGroupForm({...groupForm, description: e.target.value})}
                      placeholder="Enter group description (optional)"
                      rows={3}
                    />
                  </div>
                </div>

                <div className="customers-modal-footer">
                  <button
                    className="modal-btn-cancel"
                    onClick={() => {
                      setShowCreateGroupModal(false);
                      setGroupForm({ name: '', description: '' });
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="modal-btn-primary"
                    onClick={handleCreateGroup}
                    disabled={saving || !groupForm.name.trim()}
                  >
                    {saving ? <Loader2 size={18} className="spinning" /> : <Plus size={18} />}
                    Create Group
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add Customers to Group Modal */}
          {showAddCustomersToGroupModal && (
            <div className="customers-modal-overlay" onClick={() => setShowAddCustomersToGroupModal(false)}>
              <div className="customers-modal" onClick={(e) => e.stopPropagation()}>
                <div className="customers-modal-header">
                  <div className="customers-modal-icon group">
                    <UserPlus size={24} />
                  </div>
                  <h2>Add to "{selectedGroup?.name}"</h2>
                  <p>Select customers to add to this group</p>
                </div>

                <div className="customers-modal-body">
                  <div className="modal-search-box">
                    <Search size={18} />
                    <input
                      type="text"
                      placeholder="Search customers..."
                      value={customerSearchInGroup}
                      onChange={(e) => setCustomerSearchInGroup(e.target.value)}
                    />
                  </div>

                  <div className="customer-select-list">
                    {customersNotInGroup.length === 0 ? (
                      <div className="select-list-empty">
                        <p>No customers available to add</p>
                      </div>
                    ) : (
                      customersNotInGroup.map(customer => (
                        <label key={customer.id} className="customer-select-item">
                          <input
                            type="checkbox"
                            checked={selectedCustomersForGroup.includes(customer.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedCustomersForGroup([...selectedCustomersForGroup, customer.id]);
                              } else {
                                setSelectedCustomersForGroup(selectedCustomersForGroup.filter(id => id !== customer.id));
                              }
                            }}
                          />
                          <div className="customer-avatar-sm">
                            {customer.firstName?.charAt(0)}{customer.lastName?.charAt(0)}
                          </div>
                          <div className="customer-select-info">
                            <span className="customer-name">{customer.firstName} {customer.lastName}</span>
                            <span className="customer-email">{customer.email}</span>
                          </div>
                          {selectedCustomersForGroup.includes(customer.id) && (
                            <Check size={18} className="check-icon" />
                          )}
                        </label>
                      ))
                    )}
                  </div>
                </div>

                <div className="customers-modal-footer">
                  <button
                    className="modal-btn-cancel"
                    onClick={() => {
                      setShowAddCustomersToGroupModal(false);
                      setSelectedCustomersForGroup([]);
                      setCustomerSearchInGroup('');
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="modal-btn-primary"
                    onClick={handleAddCustomersToGroup}
                    disabled={saving || selectedCustomersForGroup.length === 0}
                  >
                    {saving ? <Loader2 size={18} className="spinning" /> : <UserPlus size={18} />}
                    Add {selectedCustomersForGroup.length} Customer(s)
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteModal.open && (
            <div className="customers-modal-overlay" onClick={() => setShowDeleteModal({ open: false, customer: null })}>
              <div className="customers-modal small" onClick={(e) => e.stopPropagation()}>
                <div className="customers-modal-header">
                  <div className="customers-modal-icon delete">
                    <Trash2 size={24} />
                  </div>
                  <h2>Delete Customer</h2>
                  <p>Are you sure you want to delete {showDeleteModal.customer?.firstName} {showDeleteModal.customer?.lastName}?</p>
                </div>

                <div className="customers-modal-body">
                  <div className="delete-warning">
                    This action cannot be undone. All data associated with this customer will be permanently removed.
                  </div>
                </div>

                <div className="customers-modal-footer">
                  <button
                    className="modal-btn-cancel"
                    onClick={() => setShowDeleteModal({ open: false, customer: null })}
                  >
                    Cancel
                  </button>
                  <button
                    className="modal-btn-delete"
                    onClick={handleDeleteCustomer}
                    disabled={saving}
                  >
                    {saving ? <Loader2 size={18} className="spinning" /> : <Trash2 size={18} />}
                    Delete Customer
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Campaign Modal */}
          {showCampaignModal && selectedCustomer && (
            <div className="customers-modal-overlay" onClick={() => setShowCampaignModal(false)}>
              <div className="customers-modal" onClick={(e) => e.stopPropagation()}>
                <div className="customers-modal-header">
                  <h2>Send Message</h2>
                  <p>{selectedCustomer.firstName} {selectedCustomer.lastName}</p>
                </div>

                <div className="campaign-type-tabs">
                  <button
                    className={`campaign-type-tab ${campaignType === 'email' ? 'active' : ''} ${!hasCampaignAccess ? 'locked' : ''}`}
                    onClick={() => hasCampaignAccess && setCampaignType('email')}
                    disabled={!hasCampaignAccess}
                  >
                    <Mail size={16} />
                    Email
                    {!hasCampaignAccess && <span className="pro-badge">PRO</span>}
                  </button>
                  <button
                    className={`campaign-type-tab ${campaignType === 'sms' ? 'active' : ''}`}
                    onClick={() => setCampaignType('sms')}
                  >
                    <Phone size={16} />
                    SMS
                  </button>
                </div>

                <div className="customers-modal-body">
                  {campaignType === 'email' && (
                    <>
                      {!selectedCustomer.email ? (
                        <div className="no-contact-notice">
                          <p>No email address available</p>
                        </div>
                      ) : (
                        <>
                          <div className="modal-form-field">
                            <label>Subject</label>
                            <input
                              type="text"
                              value={emailForm.subject}
                              onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                              placeholder="Email subject"
                            />
                          </div>
                          <div className="modal-form-field">
                            <label>Message</label>
                            <textarea
                              value={emailForm.message}
                              onChange={(e) => setEmailForm({...emailForm, message: e.target.value})}
                              placeholder="Write your message..."
                              rows={4}
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {campaignType === 'sms' && (
                    <>
                      {!selectedCustomer.phone ? (
                        <div className="no-contact-notice">
                          <p>No phone number available</p>
                        </div>
                      ) : (
                        <div className="modal-form-field">
                          <label>Message <span className="char-count">{smsForm.message.length}/160</span></label>
                          <textarea
                            value={smsForm.message}
                            onChange={(e) => e.target.value.length <= 160 && setSmsForm({...smsForm, message: e.target.value})}
                            placeholder="Write your SMS..."
                            rows={3}
                            maxLength={160}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="customers-modal-footer">
                  <button
                    className="modal-btn-cancel"
                    onClick={() => {
                      setShowCampaignModal(false);
                      setEmailForm({ subject: '', message: '' });
                      setSmsForm({ message: '' });
                    }}
                  >
                    Cancel
                  </button>
                  {campaignType === 'email' && selectedCustomer.email && (
                    <button
                      className="modal-btn-primary"
                      onClick={handleSendEmail}
                      disabled={sendingMessage || !emailForm.subject.trim() || !emailForm.message.trim()}
                    >
                      {sendingMessage ? <Loader2 size={18} className="spinning" /> : <Send size={18} />}
                      Send
                    </button>
                  )}
                  {campaignType === 'sms' && selectedCustomer.phone && (
                    <button
                      className="modal-btn-primary"
                      onClick={handleSendSMS}
                      disabled={sendingMessage || !smsForm.message.trim()}
                    >
                      {sendingMessage ? <Loader2 size={18} className="spinning" /> : <Send size={18} />}
                      Send
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tags Modal */}
          {showTagsModal && selectedCustomer && (
            <div className="customers-modal-overlay" onClick={() => setShowTagsModal(false)}>
              <div className="customers-modal small" onClick={(e) => e.stopPropagation()}>
                <div className="customers-modal-header">
                  <h2>Manage Tags</h2>
                  <p>{selectedCustomer.firstName} {selectedCustomer.lastName}</p>
                </div>

                <div className="customers-modal-body">
                  <div className="tags-list-editable">
                    {customerTags.length === 0 ? (
                      <p className="no-tags-text">No tags yet</p>
                    ) : (
                      customerTags.map((tag, i) => (
                        <span key={i} className="tag-chip">
                          {tag}
                          <button className="tag-remove-btn" onClick={() => handleRemoveTag(tag)}>
                            <X size={12} />
                          </button>
                        </span>
                      ))
                    )}
                  </div>

                  <div className="add-tag-input">
                    <input
                      type="text"
                      value={newTagInput}
                      onChange={(e) => setNewTagInput(e.target.value)}
                      placeholder="Add tag..."
                      onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                    />
                    <button className="add-tag-btn" onClick={handleAddTag} disabled={!newTagInput.trim()}>
                      <Plus size={16} />
                    </button>
                  </div>
                </div>

                <div className="customers-modal-footer">
                  <button className="modal-btn-cancel" onClick={() => setShowTagsModal(false)}>
                    Cancel
                  </button>
                  <button className="modal-btn-primary" onClick={handleSaveTags} disabled={savingTags}>
                    {savingTags ? <Loader2 size={18} className="spinning" /> : <Check size={18} />}
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Bulk action bar ─────────────────────────────
              Sticky at the bottom of the viewport when 1+ customers
              are checked. Actions:
                - Add to group   — always available (groups aren't gated)
                - Add tags       — feature-gated on marketing.tags (locked-with-PRO badge)
                - Delete         — always available, confirms first
                - Send campaign  — feature-gated on marketing.email (locked-with-PRO badge)
              Send-campaign is a per-customer flow today (needs one selectedCustomer);
              rather than rewire the whole campaign modal, we hide the bulk button
              until that plumbing is refactored. Merchants still see the per-row
              action + PRO badge on unlocked features via the quick-action buttons
              in the header.
          */}
          {bulkSelectedIds.size > 0 && (
            <div className="cv2-bulkbar" role="region" aria-label="Bulk actions">
              <div className="cv2-bulkbar__count">
                <strong>{bulkSelectedIds.size}</strong> selected
                <button
                  type="button"
                  className="cv2-bulkbar__clear"
                  onClick={clearBulk}
                >
                  Clear
                </button>
              </div>
              <div className="cv2-bulkbar__actions">
                <button
                  type="button"
                  className="cv2-btn cv2-btn--primary"
                  onClick={openBulkAddToGroup}
                  disabled={safeGroups.length === 0}
                  title={safeGroups.length === 0 ? 'Create a group first' : 'Add selected to a group'}
                >
                  <Plus size={14} /> Add to group
                </button>
                <button
                  type="button"
                  className={`cv2-btn ${!hasTagAccess ? 'is-locked' : ''}`}
                  onClick={() => hasTagAccess && setShowBulkTagModal(true)}
                  disabled={!hasTagAccess}
                  title={hasTagAccess ? 'Tag selected customers' : 'Upgrade to PRO for tag management'}
                >
                  <Tag size={14} /> Add tags
                  {!hasTagAccess && <span className="cv2-pro-badge">PRO</span>}
                </button>
                <button
                  type="button"
                  className="cv2-btn cv2-btn--danger"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                >
                  <Trash2 size={14} /> {bulkDeleting ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          )}

          {/* ── Bulk tag modal ─────────────────────────────
              Small inline modal so we don't rewire the per-customer
              tags modal (which is customer-scoped). Feature-gate re-checked
              here as defense-in-depth even though the button is disabled
              when unlocked. */}
          {showBulkTagModal && hasTagAccess && (
            <div className="customers-modal-overlay" onClick={() => setShowBulkTagModal(false)}>
              <div className="customers-modal customers-modal--sm" onClick={(e) => e.stopPropagation()}>
                <div className="customers-modal__head">
                  <h3>Add tags to {bulkSelectedIds.size} customer(s)</h3>
                  <button className="cv2-iconbtn" onClick={() => setShowBulkTagModal(false)} aria-label="Close">
                    <X size={16} />
                  </button>
                </div>
                <div className="customers-modal__body">
                  <label className="customers-modal__label" htmlFor="cv2-bulk-tags">
                    Tags <small>(comma-separated)</small>
                  </label>
                  <input
                    id="cv2-bulk-tags"
                    type="text"
                    className="customers-modal__input"
                    placeholder="e.g. VIP, wholesale, follow-up"
                    value={bulkTagsInput}
                    onChange={(e) => setBulkTagsInput(e.target.value)}
                    autoFocus
                  />
                  <p className="customers-modal__hint">
                    New tags are added to what each customer already has — existing tags aren't removed.
                  </p>
                </div>
                <div className="customers-modal__foot">
                  <button className="cv2-btn" onClick={() => setShowBulkTagModal(false)} disabled={bulkTagging}>
                    Cancel
                  </button>
                  <button
                    className="cv2-btn cv2-btn--primary"
                    onClick={applyBulkTags}
                    disabled={bulkTagging || !bulkTagsInput.trim()}
                  >
                    {bulkTagging ? 'Tagging…' : `Apply to ${bulkSelectedIds.size}`}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Bulk add-to-group modal ────────────────────
              Purpose-built for the "I have people, pick a group"
              flow. Preselects the first group so the merchant can
              one-click through when there's only one. */}
          {showBulkGroupModal && (
            <div className="customers-modal-overlay" onClick={() => setShowBulkGroupModal(false)}>
              <div className="customers-modal customers-modal--sm" onClick={(e) => e.stopPropagation()}>
                <div className="customers-modal__head">
                  <h3>Add {bulkSelectedIds.size} customer(s) to a group</h3>
                  <button className="cv2-iconbtn" onClick={() => setShowBulkGroupModal(false)} aria-label="Close">
                    <X size={16} />
                  </button>
                </div>
                <div className="customers-modal__body">
                  {safeGroups.length === 0 ? (
                    <p className="customers-modal__hint">
                      No groups yet. Create one from the <strong>Groups</strong> tab first.
                    </p>
                  ) : (
                    <>
                      <label className="customers-modal__label" htmlFor="cv2-bulk-group">
                        Group
                      </label>
                      <select
                        id="cv2-bulk-group"
                        className="customers-modal__input"
                        value={bulkGroupId}
                        onChange={(e) => setBulkGroupId(e.target.value)}
                      >
                        {safeGroups.map(g => (
                          <option key={g.id} value={g.id}>
                            {g.name} ({g.customers?.length || 0})
                          </option>
                        ))}
                      </select>
                      <p className="customers-modal__hint">
                        Customers already in this group are skipped automatically.
                      </p>
                    </>
                  )}
                </div>
                <div className="customers-modal__foot">
                  <button className="cv2-btn" onClick={() => setShowBulkGroupModal(false)} disabled={bulkAddingToGroup}>
                    Cancel
                  </button>
                  <button
                    className="cv2-btn cv2-btn--primary"
                    onClick={applyBulkAddToGroup}
                    disabled={bulkAddingToGroup || !bulkGroupId || safeGroups.length === 0}
                  >
                    {bulkAddingToGroup ? 'Adding…' : `Add ${bulkSelectedIds.size}`}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </FeatureGate>
    </Shell>
  );
}
